import {
  ChevronDown,
  ChevronUp,
  Download,
} from "lucide-react";
import { type ReactNode, useState } from "react";

import type {
  PrebuildReviewQuestion,
  PrebuildReviewSourceFile,
} from "@/lib/api/databook";

import {
  QuestionAnswerField,
  type QuestionAnswerAttachment,
} from "./QuestionAnswerField";
import { formatQuestionMoney } from "./questionText";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface QuestionsStageProps {
  questions: PrebuildReviewQuestion[];
  answers: Record<string, string>;
  attachments?: Record<string, QuestionAnswerAttachment[]>;
  uploadingQuestionIds?: string[];
  processing: boolean;
  failed: boolean;
  canEdit: boolean;
  canRetry?: boolean;
  submitted?: boolean;
  answersChanged?: boolean;
  onAnswer: (questionId: string, answer: string) => void;
  onAttach?: (questionId: string, files: File[]) => void;
  onDetach?: (questionId: string, documentId: string) => void;
  onDownloadAttachment?: (attachment: QuestionAnswerAttachment) => void;
  onContinue: () => void;
  onViewBasis: () => void;
  onRetry: () => void;
  onBack: () => void;
  onDownloadSource?: (source: PrebuildReviewSourceFile) => Promise<void> | void;
  backLabel?: string;
  continueLabel?: string;
  children?: ReactNode;
}

const EMPTY_ATTACHMENTS: Record<string, QuestionAnswerAttachment[]> = {};
const EMPTY_UPLOADING_QUESTION_IDS: string[] = [];
const COLLAPSED_SHEET_COUNT = 4;
const COLLAPSED_SOURCE_FILE_COUNT = 6;

interface QuestionSourceFile {
  document_id: string;
  filename: string;
  sheets: string[];
  wholeFile: boolean;
  downloadSource: PrebuildReviewSourceFile;
}

function questionSourceFiles(
  sources: PrebuildReviewSourceFile[],
): QuestionSourceFile[] {
  const files = new Map<string, QuestionSourceFile>();
  for (const source of sources) {
    const file = files.get(source.document_id) ?? {
      document_id: source.document_id,
      filename: source.filename,
      sheets: [],
      wholeFile: false,
      downloadSource: source,
    };
    if (source.sheet_name == null) {
      file.wholeFile = true;
    } else if (!file.sheets.includes(source.sheet_name)) {
      file.sheets.push(source.sheet_name);
    }
    files.set(source.document_id, file);
  }
  return Array.from(files.values());
}

export function questionPresentation(question: PrebuildReviewQuestion) {
  const sentences = question.text
    .trim()
    .split(/(?<=[.!?])\s+/)
    .filter(Boolean);
  let questionIndex = -1;
  for (let index = sentences.length - 1; index >= 0; index -= 1) {
    if (sentences[index].endsWith("?")) {
      questionIndex = index;
      break;
    }
  }
  const title = questionIndex >= 0
    ? sentences[questionIndex]
    : question.text;
  const context = questionIndex >= 0
    ? sentences.filter((_, index) => index !== questionIndex).join(" ")
    : "";
  return {
    title: formatQuestionMoney(title),
    context: formatQuestionMoney(context),
  };
}

function SourceFileCard({
  source,
  onDownload,
}: {
  source: QuestionSourceFile;
  onDownload?: (source: PrebuildReviewSourceFile) => Promise<void> | void;
}) {
  const [expanded, setExpanded] = useState(false);
  const hiddenSheetCount = Math.max(
    source.sheets.length - COLLAPSED_SHEET_COUNT,
    0,
  );
  const visibleSheets = expanded
    ? source.sheets
    : source.sheets.slice(0, COLLAPSED_SHEET_COUNT);

  return (
    <div className="h-full min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1">
      <div className="flex min-w-0 items-start gap-1.5">
        <span
          className="block min-w-0 flex-1 whitespace-normal break-words font-medium text-foreground"
          style={{ fontSize: 10.5 }}
          title={source.filename}
        >
          {source.filename}
        </span>
        {onDownload && (
          <button
            type="button"
            onClick={() => void onDownload(source.downloadSource)}
            title={`Download ${source.filename}`}
            aria-label={`Download ${source.filename}`}
            className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Download aria-hidden="true" size={10} />
          </button>
        )}
      </div>

      {source.wholeFile && source.sheets.length === 0 ? (
        <div className="mt-0.5 leading-none text-muted-foreground" style={{ fontSize: 8.5 }}>
          Whole file
        </div>
      ) : (
        <div className="mt-0.5 flex flex-wrap items-center gap-0.5 leading-none">
          <span
            className="font-semibold uppercase tracking-wider text-muted-foreground"
            style={{ fontSize: 7.5 }}
          >
            Sheets
          </span>
          {visibleSheets.map((sheet) => (
            <span
              key={sheet}
              className="max-w-[150px] truncate px-1 py-0 text-foreground/70"
              style={{ fontSize: 8.5 }}
              title={sheet}
            >
              {sheet}
            </span>
          ))}
          {hiddenSheetCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((current) => !current)}
              className="inline-flex items-center gap-0.5 rounded px-1 py-0 font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
              style={{ fontSize: 8.5 }}
            >
              {expanded ? "Show less" : `+${hiddenSheetCount} more`}
              {expanded
                ? <ChevronUp aria-hidden="true" size={9} />
                : <ChevronDown aria-hidden="true" size={9} />}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function QuestionSources({
  sources,
  onDownload,
}: {
  sources: QuestionSourceFile[];
  onDownload?: (source: PrebuildReviewSourceFile) => Promise<void> | void;
}) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const hiddenFileCount = Math.max(
    sources.length - COLLAPSED_SOURCE_FILE_COUNT,
    0,
  );
  const visibleSources = expanded
    ? sources
    : sources.slice(0, COLLAPSED_SOURCE_FILE_COUNT);
  const sourceCards = visibleSources.map((source) => (
    <SourceFileCard
      key={source.document_id}
      source={source}
      onDownload={onDownload}
    />
  ));

  return (
    <div className="mt-4 border-t border-border/60 pt-3">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            aria-label={`${open ? "Collapse" : "Expand"} source files`}
            className="mb-2 flex w-full items-center justify-between rounded-md text-left text-muted-foreground hover:text-foreground"
            style={{ fontSize: 10 }}
          >
            <span>
              Sources · {sources.length} {sources.length === 1 ? "file" : "files"}
            </span>
            {open
              ? <ChevronUp aria-hidden="true" size={12} />
              : <ChevronDown aria-hidden="true" size={12} />}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {sources.length === 2 ? (
            <div className="grid items-stretch gap-1.5 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
              {sourceCards[0]}
              <span className="self-center text-center font-medium text-muted-foreground" style={{ fontSize: 10 }}>versus</span>
              {sourceCards[1]}
            </div>
          ) : (
            <div className="grid auto-rows-fr gap-1.5 sm:grid-cols-2 md:grid-cols-3">{sourceCards}</div>
          )}
          {hiddenFileCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((current) => !current)}
              aria-expanded={expanded}
              className="mt-2 inline-flex items-center gap-1 rounded-md px-2 py-1 font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
              style={{ fontSize: 10 }}
            >
              {expanded
                ? "Show fewer files"
                : `Show ${hiddenFileCount} more ${hiddenFileCount === 1 ? "file" : "files"}`}
              {expanded
                ? <ChevronUp aria-hidden="true" size={11} />
                : <ChevronDown aria-hidden="true" size={11} />}
            </button>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

export function QuestionsStage({
  questions,
  answers,
  attachments = EMPTY_ATTACHMENTS,
  uploadingQuestionIds = EMPTY_UPLOADING_QUESTION_IDS,
  processing,
  failed,
  canEdit,
  canRetry = canEdit,
  submitted = false,
  answersChanged = false,
  onAnswer,
  onAttach,
  onDetach,
  onDownloadAttachment,
  onContinue,
  onViewBasis,
  onRetry,
  onBack,
  onDownloadSource,
  backLabel = "← Back to uploads",
  continueLabel = "Continue",
  children,
}: QuestionsStageProps) {
  const answeredCount = questions.filter(
    (question) => (
      answers[question.id]?.trim()
      || (attachments[question.id]?.length ?? 0) > 0
    ),
  ).length;
  const uploading = uploadingQuestionIds.length > 0;
  const complete =
    questions.length > 0
    && answeredCount === questions.length;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="builder-stage-frame pb-10 pt-7">
        <div className="mb-5 flex items-end justify-between gap-4">
          <div className="text-2xl font-semibold tracking-tight">Questions</div>
          <span className="text-muted-foreground/70" style={{ fontSize: 10.5 }}>
            {answeredCount} of {questions.length} answered
          </span>
        </div>

        {questions.length > 0 && (
          <div className="mb-5 space-y-3">
            {questions.map((question) => {
              const presentation = questionPresentation(question);
              const questionAttachments = attachments[question.id] ?? [];
              const questionUploading = uploadingQuestionIds.includes(question.id);
              const answered = Boolean(
                answers[question.id]?.trim() || questionAttachments.length > 0,
              );
              const sourceFiles = questionSourceFiles(question.source_files);
              return (
                <section
                  key={question.id}
                  className={`overflow-hidden rounded-xl border shadow-[0_1px_2px_rgba(0,0,0,0.025)] transition-colors ${
                    answered
                      ? "border-border/70 bg-muted/40"
                      : "border-border bg-card"
                  }`}
                >
                  <div className="px-5 pb-4 pt-4">
                    <div className="min-w-0">
                      <h2 className="font-semibold leading-snug text-foreground" style={{ fontSize: 15 }}>
                        {presentation.title}
                      </h2>
                      {presentation.context && (
                        <p className="mt-1.5 leading-relaxed text-muted-foreground" style={{ fontSize: 11.5 }}>
                          {presentation.context}
                        </p>
                      )}
                    </div>

                    {sourceFiles.length > 0 && (
                      <QuestionSources
                        sources={sourceFiles}
                        onDownload={onDownloadSource}
                      />
                    )}
                  </div>
                  <div className="border-t border-border/60 bg-muted/20 px-5 py-3.5">
                    <QuestionAnswerField
                      value={answers[question.id] || ""}
                      suggestedAnswers={question.suggested_answers}
                      attachments={questionAttachments}
                      uploading={questionUploading}
                      disabled={!canEdit || processing}
                      onChange={(answer) => onAnswer(question.id, answer)}
                      onAttach={canEdit && onAttach
                        ? (files) => onAttach(question.id, files)
                        : undefined}
                      onDetach={onDetach
                        ? (documentId) => onDetach(question.id, documentId)
                        : undefined}
                      onDownloadAttachment={onDownloadAttachment}
                    />
                  </div>
                </section>
              );
            })}
          </div>
        )}

        {children}

        {failed && (
          <div className="mb-4 rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3">
            <div className="font-semibold" style={{ fontSize: 12 }}>
              Answers could not be applied
            </div>
            <button
              type="button"
              onClick={onRetry}
              disabled={!canRetry}
              className="mt-2 rounded-lg border border-border bg-background px-3 py-1.5 font-medium disabled:opacity-40"
              style={{ fontSize: 12.5 }}
            >
              Retry
            </button>
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onBack}
            disabled={processing}
            className="rounded-lg border border-border px-4 py-2 font-medium text-foreground/80 hover:bg-muted disabled:opacity-40"
            style={{ fontSize: 12.5 }}
          >
            {backLabel}
          </button>
          <button
            type="button"
            onClick={processing ? onViewBasis : onContinue}
            disabled={
              !processing && (
                uploading
                || failed
                || !complete
                || (!submitted && !canEdit)
                || (answersChanged && !canEdit)
              )
            }
            className="flex items-center gap-1.5 rounded-lg bg-foreground px-4 py-2 font-medium text-background hover:opacity-90 disabled:opacity-40"
            style={{ fontSize: 12.5 }}
          >
            {continueLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
